import apiClient, { API_BASE_URL } from './apiClient';

// ============================================================
// Types — matching backend Go models (FC120, FC121, FC122)
// ============================================================

export interface FC122 {
  fc122_01: string;   // 交運單號
  fc122_02: number;   // 產品項次
  fc122_03: number;   // 批號項次
  fc122_11: string;   // 批號
  fc122_12: number;   // 交運數量
  fc122_14: string;   // 交運單位
  fc122_15: number;   // 單位換算率
  fc122_17: string;   // 庫存單位
  fc122_18: number;   // 換算後數量
  fc122_22: boolean;  // 補布標記
}

export interface FC121 {
  fc121_01: string;   // 交運單號
  fc121_02: number;   // 產品項次
  fc121_10: string;   // S/C No
  fc121_11: string;   // 產品代號1
  fc121_12: string;   // 色號
  fc121_13: string;   // 產品規格
  fc121_14: number;   // S/C項次
  fc121_15: string;   // 單位
  fc121_16: number;   // 交運數量
  fc121_17: string;   // 交運單位
  fc121_18: number;   // 交運單價
  fc121_181: number;
  fc121_19: number;   // 交運金額
  fc121_20: number;   // S/C數量
  fc121_21: number;   // S/C單價
  fc121_22: string;   // 色號名稱
  fc121_23: number;   // 色號項次
  fc121_24: string;   // 客戶產品代號
  fc121_25: string;   // 客戶色號
  fc121_26: string;   // 異常標記
  fc121_27: string;   // 用途代碼
  fc121_28: number;   // 布品分類
  fc121_29: number;   // 色號數
  fc121_30: number;   // 補布數量
  batches?: FC122[];
}

export interface FC123 {
  fc123_01: string;        // 交運單號
  fc123_02: string;        // 繳款單號
  fc123_11: string | null; // 繳款日期
  fc123_12: number;        // 繳款金額
}

export interface FC126 {
  fc126_01: string;        // 交運單號
  fc126_02: string;        // 發票號碼
  fc126_11: string | null; // 發票日期
  fc126_12: number;        // 發票金額
}

export interface FC120 {
  fc120_01: string;        // 交運單號 (PK)
  fc120_10: string;        // S/C No
  fc120_11: string | null; // 交運日期
  fc120_12: string;        // 客戶代號
  fc120_13: number;        // 交運類別 (1=正常,2=異常)
  fc120_14: string;        // 庫位代號
  fc120_15: string;        // 狀態旗標
  fc120_16: number;        // 稅額
  fc120_17: string;        // 部門代號
  fc120_18: string;        // 幣別
  fc120_19: number;        // 匯率
  fc120_20: number;        // 交運淨額
  fc120_201: number;       // 含稅總額
  fc120_21: string;        // 收貨公司代號
  fc120_22: string;        // 審核狀態旗標
  fc120_23: string;        // 收貨地址
  fc120_24: string;        // 異常旗標
  fc120_27: number;        // 明細總行數
  fc120_28: string | null; // 最晚繳庫日期
  fc120_29: boolean;       // 開立發票標記
  fc120_291: number;       // 發票種類
  fc120_30: string | null; // 報關日期
  fc120_31: number;        // 報關匯率
  fc120_32: string | null; // 建檔日期
  fc120_40: string;        // 異常說明-業務
  fc120_41: string;        // 異常說明-主管
  fc120_43: boolean;       // 新增旗標
  fc120_44: string;        // 轉客戶寄庫庫位
  fc120_48: string;        // 出貨通知單號
  fc120_49: string;        // 客訴單號
  fc120_80: string;        // 審核狀態
  fc120_91: string;        // 修改狀態/立單人
  fc120_92: string | null; // 修改時間
  fc120_93: string;        // 修改人員
  fc120_94: string;        // 輸單人員
  details?: FC121[];
  payments?: FC123[];
  invoices?: FC126[];
}

// ============================================================
// Request / Response types
// ============================================================

export interface C204ListResponse {
  data: FC120[];
  total: number;
  page: number;
  size: number;
}

export interface C204CreateRequest {
  master: Partial<FC120>;
  details: Partial<FC121>[];
}

export interface ValidationError {
  field: string;
  message: string;
}

// ============================================================
// API methods
// ============================================================

export const c204Api = {
  async list(page = 1, size = 20, keyword?: string): Promise<C204ListResponse> {
    const params: Record<string, string | number> = { page, size };
    if (keyword) params.keyword = keyword;
    const res = await apiClient.get('/api/pwc204f1', { params });
    return res.data;
  },

  async get(doNo: string): Promise<FC120> {
    const res = await apiClient.get(`/api/pwc204f1/${encodeURIComponent(doNo)}`);
    return res.data;
  },

  async importFromSC(scNo: string, deliveryDate?: string): Promise<FC120> {
    const params = deliveryDate ? { deliveryDate: deliveryDate.slice(0, 10) } : undefined;
    const res = await apiClient.get(`/api/pwc204f1/from-sc/${encodeURIComponent(scNo)}`, { params });
    return res.data;
  },

  async create(data: C204CreateRequest): Promise<FC120> {
    const res = await apiClient.post('/api/pwc204f1', data);
    return res.data;
  },

  async update(doNo: string, data: C204CreateRequest): Promise<FC120> {
    const res = await apiClient.put(`/api/pwc204f1/${encodeURIComponent(doNo)}`, data);
    return res.data;
  },

  async delete(doNo: string): Promise<void> {
    await apiClient.delete(`/api/pwc204f1/${encodeURIComponent(doNo)}`);
  },

  async updateStatus(doNo: string, status: string): Promise<void> {
    await apiClient.patch(`/api/pwc204f1/${encodeURIComponent(doNo)}/status`, { status });
  },

  async lookupName(type: string, code: string): Promise<string> {
    if (!code || !code.trim()) return '';
    try {
      const res = await apiClient.get(
        `/api/pwc204f1/lookup/${encodeURIComponent(type)}/${encodeURIComponent(code.trim())}`
      );
      return res.data.name || '';
    } catch {
      return '';
    }
  },

  async submit(doNo: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/pwc204f1/${encodeURIComponent(doNo)}/submit`);
    return res.data;
  },

  async approve(doNo: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/pwc204f1/${encodeURIComponent(doNo)}/approve`);
    return res.data;
  },

  async reject(doNo: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/pwc204f1/${encodeURIComponent(doNo)}/reject`);
    return res.data;
  },

  async writeoff(doNo: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/pwc204f1/${encodeURIComponent(doNo)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  getPrintUrl(doNo: string): string {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    const params = new URLSearchParams({ token });
    return `${API_BASE_URL}/api/pwc204f1/${encodeURIComponent(doNo)}/print?${params.toString()}`;
  },

  async getHistory(doNo: string): Promise<ApprovalEvent[]> {
    const res = await apiClient.get(`/api/pwc204f1/${encodeURIComponent(doNo)}/history`);
    return res.data.events || [];
  },
};

// ============================================================
// Approval history types
// ============================================================

export interface ApprovalEvent {
  action: string;
  user: string;
  date: string | null;
  note: string;
}
